import { useEffect, useState } from 'react';
import { scApi, type MatchDetail, type SGroup } from '../sportcenter';
import { hubApi, useHideScores } from '../sporthub';
import { Crest } from './SportCenterBits';

/**
 * Sport in de Live TV-speler: speelt deze zender een wedstrijd, dan de stand in de infobalk en een
 * wedstrijdcentrum aan de zijkant (stats, verloop, opstelling, stand), zoals een sport-tv-kanaal dat doet.
 */

export function useChannelMatch(accountId: string, streamId: number, catchup: boolean) {
  const [match, setMatch] = useState<{ path: string; eventId: string } | null>(null);
  const [detail, setDetail] = useState<MatchDetail | null>(null);
  useEffect(() => {
    if (catchup) return;
    let alive = true;
    const find = () => hubApi.onChannel(accountId, streamId).then((r) => alive && setMatch(r.match)).catch(() => undefined);
    void find();
    const t = window.setInterval(find, 3 * 60_000);
    return () => { alive = false; window.clearInterval(t); setMatch(null); setDetail(null); };
  }, [accountId, streamId, catchup]);
  useEffect(() => {
    if (!match) return;
    let alive = true;
    const load = () => scApi.match(match.path, match.eventId).then((d) => alive && setDetail(d)).catch(() => undefined);
    void load();
    const t = window.setInterval(load, 20_000);
    return () => { alive = false; window.clearInterval(t); };
  }, [match]);
  return detail;
}

/** Kleine stand voor de infobalk ("AJA 2-1 INT 63'"). */
export function ScoreChip({ d }: { d: MatchDetail }) {
  const hide = useHideScores();
  const m = d.match;
  return (
    <span className="sp-chip" aria-label={`${m.home.short} tegen ${m.away.short}${hide ? '' : `, ${m.home.score ?? 0}-${m.away.score ?? 0}`}`}>
      <b style={{ background: m.home.color ? `#${m.home.color}` : undefined }}>{m.home.abbr || m.home.short.slice(0, 3)}</b>
      <i>{m.state === 'pre' ? 'vs' : hide ? '•–•' : `${m.home.score ?? 0}-${m.away.score ?? 0}`}</i>
      <b style={{ background: m.away.color ? `#${m.away.color}` : undefined }}>{m.away.abbr || m.away.short.slice(0, 3)}</b>
      {m.state === 'in' && <em>{m.minute ?? m.detail}</em>}
    </span>
  );
}

type Tab = 'stats' | 'verloop' | 'opstelling' | 'stand';

export function SportPanel({ d, onClose }: { d: MatchDetail; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('stats');
  const [table, setTable] = useState<SGroup[] | null>(null);
  const hide = useHideScores();
  const m = d.match;
  useEffect(() => {
    if (tab !== 'stand' || table) return;
    scApi.standings({ path: m.path }).then((r) => setTable(r.groups)).catch(() => setTable([]));
  }, [tab, table, m.path]);
  const tabs: [Tab, string][] = [['stats', 'Stats'], ['verloop', 'Verloop'], ['opstelling', 'Opstelling'], ['stand', 'Stand']];
  return (
    <aside className="sp-panel" aria-label="Wedstrijdcentrum" onClick={(e) => e.stopPropagation()}>
      <div className="sp-head">
        <span className="sp-title">Wedstrijd<span>centrum</span></span>
        <button className="icon-mini" onClick={onClose} aria-label="Sluiten">✕</button>
      </div>
      <div className="sp-score">
        <span><Crest logo={m.home.logo} name={m.home.short} size={40} />{m.home.short}</span>
        <b className={hide ? 'is-hidden' : ''} data-score>{m.state === 'pre' ? 'vs' : `${m.home.score ?? 0} – ${m.away.score ?? 0}`}</b>
        <span>{m.away.short}<Crest logo={m.away.logo} name={m.away.short} size={40} /></span>
      </div>
      <nav className="sp-tabs" data-focus-group>
        {tabs.map(([id, label]) => <button key={id} type="button" aria-pressed={tab === id} onClick={() => setTab(id)}>{label}</button>)}
      </nav>
      <div className="sp-body">
        {tab === 'stats' && (d.stats.length ? d.stats.map((s) => {
          const t = (s.homeN + s.awayN) || 1;
          return (
            <div key={s.label} className="sp-stat">
              <span className="sp-stat-row"><b>{s.home}</b><span>{s.label}</span><b>{s.away}</b></span>
              <span className="sp-bar"><i style={{ width: `${(s.homeN / t) * 100}%` }} /><i className="is-away" /></span>
            </div>
          );
        }) : <p className="subtle">Nog geen statistieken.</p>)}
        {tab === 'verloop' && (hide ? <p className="subtle">Uitslagen zijn verborgen.</p> : d.events.length ? (
          <ul className="sp-events">{d.events.filter((e) => e.kind !== 'other').map((e, i) => <li key={i} className={`sp-ev-${e.side ?? 'mid'} sp-k-${e.kind}`}><b>{e.minute}</b> {e.player ?? e.text}{e.extra ? <span className="subtle"> · {e.extra}</span> : null}</li>)}</ul>
        ) : <p className="subtle">Nog niets gebeurd.</p>)}
        {tab === 'opstelling' && (d.lineups.length ? d.lineups.map((l) => (
          <div key={l.side} className="sp-lineup">
            <b>{l.side === 'home' ? m.home.short : m.away.short}{l.formation ? ` · ${l.formation}` : ''}</b>
            <ol>{l.starters.map((p) => <li key={`${p.n}${p.name}`}><i>{p.n}</i> {p.name}{p.subOut ? ' ↓' : ''}</li>)}</ol>
            {l.bench.length > 0 && <span className="subtle">Wissels: {l.bench.map((p) => `${p.name}${p.subIn ? ' ↑' : ''}`).join(', ')}</span>}
          </div>
        )) : <p className="subtle">Opstellingen zijn er nog niet.</p>)}
        {tab === 'stand' && (!table ? <p className="subtle">Laden…</p> : table.length ? table.slice(0, 1).map((g) => (
          <table key={g.name} className="sp-table">
            <thead><tr><th>#</th><th>Club</th><th>G</th><th>DS</th><th>P</th></tr></thead>
            <tbody>{g.rows.map((r) => <tr key={r.team.id} className={r.team.id === m.home.id || r.team.id === m.away.id ? 'is-here' : ''}><td>{r.rank}</td><td>{r.team.short}</td><td>{r.played}</td><td>{r.gd}</td><td><b>{r.points}</b></td></tr>)}</tbody>
          </table>
        )) : <p className="subtle">Geen stand voor deze competitie.</p>)}
      </div>
    </aside>
  );
}
